import * as PATHS from '@/constants/path';
import { request } from '@/services/api/request';
import { MailboxConnectionFilter, MailThreadScope } from '@/typings/models/Email';
import { URLParamBuilder } from '@/utils/functions';

// office365_mail — all reads/sends go to Graph live through Laravel; the only
// caching is a short server-side TTL. `rescan=1` bypasses it.

// `search` is answered by Graph itself, so it reaches message bodies and every
// folder — not just the threads an earlier sweep returned. Searches are never
// cached server-side; each one is a live sweep.
export function fetchLeadMailThreads(
  leadId: string,
  rescan = false,
  scope?: MailThreadScope,
  search?: string,
) {
  const url = URLParamBuilder(PATHS.LEAD_MAIL_THREADS, { params: { leadId } });
  return request<any>(url, {
    method: 'GET',
    params: {
      ...(rescan ? { rescan: 1 } : {}),
      ...(scope ? { scope } : {}),
      ...(search ? { search } : {}),
    },
  });
}

export function fetchLeadThreadMessages(leadId: string, mailbox: string, conversationId: string) {
  const url = URLParamBuilder(PATHS.LEAD_MAIL_THREAD_MESSAGES, { params: { leadId } });
  return request<any>(url, {
    method: 'GET',
    params: { mailbox, conversation_id: conversationId },
  });
}

export function fetchLeadMailMessage(leadId: string, messageId: string, mailbox: string) {
  const url = URLParamBuilder(PATHS.LEAD_MAIL_MESSAGE, {
    params: { leadId, messageId: encodeURIComponent(messageId) },
  });
  return request<any>(url, { method: 'GET', params: { mailbox } });
}

export function getLeadMailAttachmentUrl(leadId: string, messageId: string, attachmentId: string, mailbox: string) {
  const url = URLParamBuilder(PATHS.LEAD_MAIL_ATTACHMENT, {
    params: {
      leadId,
      messageId: encodeURIComponent(messageId),
      attachmentId: encodeURIComponent(attachmentId),
    },
  });
  return `${url}?mailbox=${encodeURIComponent(mailbox)}`;
}

export function sendLeadMailRequest(leadId: string, data: { subject: string; html: string; to?: string[]; cc?: string[] }) {
  const url = URLParamBuilder(PATHS.LEAD_MAIL_SEND, { params: { leadId } });
  return request<any>(url, { method: 'POST', data });
}

export function replyLeadMailRequest(leadId: string, messageId: string, data: { mailbox: string; html: string; reply_all?: boolean }) {
  const url = URLParamBuilder(PATHS.LEAD_MAIL_REPLY, {
    params: { leadId, messageId: encodeURIComponent(messageId) },
  });
  return request<any>(url, { method: 'POST', data });
}

export function fetchMyMailboxRequest() {
  return request<any>(URLParamBuilder(PATHS.MAIL_MY_MAILBOX), { method: 'GET' });
}

export function fetchOffice365MailCapabilitiesRequest() {
  return request<any>(URLParamBuilder(PATHS.MAIL_CAPABILITIES), { method: 'GET' });
}

// ── monitored-mailbox directory (integration settings) ──────────────────────

export function fetchMonitoredMailboxesRequest(
  params: { search?: string; limit?: number } = {},
) {
  return request<any>(URLParamBuilder(PATHS.MAIL_MAILBOXES), { method: 'GET', params });
}

export function syncMailboxDirectoryRequest() {
  return request<any>(URLParamBuilder(PATHS.MAIL_MAILBOXES_SYNC), { method: 'POST' });
}

export function addMonitoredMailboxRequest(address: string) {
  return request<any>(URLParamBuilder(PATHS.MAIL_MAILBOXES), {
    method: 'POST',
    data: { address },
  });
}

export function removeMonitoredMailboxRequest(address: string) {
  const url = URLParamBuilder(PATHS.MAIL_MAILBOX, {
    params: { address: encodeURIComponent(address) },
  });
  return request<any>(url, { method: 'DELETE' });
}

// `connection` narrows to users the send-as ladder does (or does not) resolve
// to a mailbox. It has to be applied server-side: the endpoint paginates over
// users, so filtering the returned page would yield short pages and a total
// that counts rows the caller never sees.
export function fetchMailboxUserStatusesRequest(
  params: {
    page?: number;
    per_page?: number;
    search?: string;
    connection?: MailboxConnectionFilter;
  } = {},
) {
  return request<any>(URLParamBuilder(PATHS.MAIL_MAILBOX_USERS), { method: 'GET', params });
}

export function setMailboxUserOverrideRequest(userUuid: string, mailboxAddress: string | null) {
  const url = URLParamBuilder(PATHS.MAIL_MAILBOX_USER_OVERRIDE, { params: { userUuid } });
  return request<any>(url, {
    method: 'PUT',
    data: { mailbox_address: mailboxAddress },
  });
}

// ── Email Center templates for "Insert template" ─────────────────────────────

export function fetchEmailTemplatesRequest() {
  return request<any>('/email-templates', { method: 'GET' });
}

export function renderEmailTemplatePreviewRequest(templateUuid: string, data: { variables?: Record<string, string>; lead_uuid?: string; language?: string }) {
  const url = URLParamBuilder(PATHS.EMAIL_TEMPLATE_RENDER_PREVIEW, {
    params: { uuid: templateUuid },
  });
  return request<any>(url, { method: 'POST', data });
}
